'use client';

import { ColumnDef } from '@tanstack/react-table';
import { DollarSign, Image, Tag, Package, ExternalLink } from 'lucide-react';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import { DataTableCheckbox } from '@/components/data-table/data-table-checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { CopyableResourceBadge } from '@/components/ui/copyable-resource-badge';
import { formatMoney } from "@/lib/money";
import { getCurrencyOption } from '@/lib/currency/currencyOptionsKeys';
import { Product } from '@tagadapay/crm-entities';
import { Link } from 'react-router-dom';
import { ResourceBadge } from '@/components/ui/resource-badge';
import { useStoreNavigate } from '@/hooks/use-app-navigate';
import { VariantResourceBadge } from '@/components/variant/VariantResourceBadge';

type VariantWithPrices = Product.ProductWithRelations['variants'][number];

interface GetVariantsTableColumnsParams {
  productId: string;
  storeId: string;
}

export const useVariantsTableColumns = ({
  productId,
  storeId,
}: GetVariantsTableColumnsParams): ColumnDef<VariantWithPrices>[] => {
  const { t } = useTranslation('products');
  return React.useMemo(() => [
  {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label={t('variantsTable.selectAll')}
        className="translate-y-0.5"
      />
    ),
    cell: ({ row }) => (
      <DataTableCheckbox row={row} />
    ),
    enableSorting: false,
    enableHiding: false,
    size: 40,
  },
  {
    id: 'image',
    accessorKey: 'imageUrl',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('variantsTable.columns.image')} />,
    cell: ({ row }) => {
      const variant = row.original;
      return (
        <div className="bg-muted flex h-10 w-10 items-center justify-center rounded border">
          {variant.imageUrl ? (
            <img src={variant.imageUrl} alt={variant.name} className="h-full w-full rounded object-cover" />
          ) : (
            <Image className="text-muted-foreground h-4 w-4" />
          )}
        </div>
      );
    },
    enableSorting: false,
    enableHiding: false,
    size: 60,
  },
  {
    id: 'name',
    accessorKey: 'name',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('variantsTable.columns.name')} />,
    cell: ({ row }) => {
      const variant = row.original;
      const { storeRoutes } = useStoreNavigate();
      return (
        <div className="min-w-0">
          <Link
            to={storeRoutes.VARIANT_DETAILS(productId, variant.id)}
            className="truncate font-medium hover:underline"
          >
            {variant.name}
          </Link>
          {variant.description && (
            <div className="text-muted-foreground max-w-[300px] truncate text-xs">{variant.description}</div>
          )}
        </div>
      );
    },
    meta: {
      label: t('variantsTable.columns.name'),
      placeholder: t('variantsTable.searchVariants'),
      variant: 'text',
      icon: () => <Package />,
    },
    enableColumnFilter: false,
    enableSorting: false,
  },

  {
    id: 'sku',
    accessorKey: 'sku',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('variantsTable.columns.sku')} />,
    cell: ({ row }) => {
      const variant = row.original;
      return <span className="text-muted-foreground">{variant.sku || '-'}</span>;
    },
    meta: {
      label: t('variantsTable.columns.sku'),
      placeholder: t('variantsTable.searchBySku'),
      variant: 'text',
      icon: () => <Tag />,
    },
    enableColumnFilter: false,
    enableSorting: false,
  },
  {
    id: 'price',
    accessorKey: 'prices',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('variantsTable.columns.price')} />,
    cell: ({ row }) => {
      const variant = row.original;
      const defaultPrice = variant.prices.find((p) => p.default) || variant.prices[0];

      if (!defaultPrice) {
        return <span className="text-muted-foreground">{t('variantsTable.noPrice')}</span>;
      }
      const resolved = getCurrencyOption(
        defaultPrice.currencyOptions,
        defaultPrice.currency || undefined,
      );
      if (!resolved || resolved.option.amount <= 0) {
        return <span className="text-muted-foreground">{t('variantsTable.noPrice')}</span>;
      }
      return (
        <div>
          <div className="font-medium">
            {formatMoney(resolved.option.amount, resolved.currency)}
          </div>
          {defaultPrice.recurring && (
            <div className="text-muted-foreground text-xs">
              {defaultPrice.interval && defaultPrice.intervalCount
                ? t('variantsTable.perInterval', {
                    count: defaultPrice.intervalCount,
                    interval: defaultPrice.interval + (defaultPrice.intervalCount > 1 ? 's' : ''),
                  })
                : t('variantsTable.recurring')}
            </div>
          )}
          {variant.prices.length > 1 && (
            <div className="text-muted-foreground text-xs">
              {t('variantsTable.moreCount', { count: variant.prices.length - 1 })}
            </div>
          )}
        </div>
      );
    },
    meta: {
      label: t('variantsTable.columns.price'),
      variant: 'range',
      icon: () => <DollarSign />,
    },
    enableColumnFilter: false,
    enableSorting: false,
  },
  {
    id: 'weight',
    accessorKey: 'grams',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('variantsTable.columns.weight')} />,
    cell: ({ row }) => {
      const variant = row.original;
      return variant.grams ? <span>{variant.grams}g</span> : <span className="text-muted-foreground">-</span>;
    },
    enableSorting: false,
    meta: { label: t('variantsTable.columns.weight') },
    size: 100,
  },
  {
    id: 'status',
    accessorKey: 'active',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('variantsTable.columns.status')} />,
    cell: ({ row }) => {
      const variant = row.original;
      return (
        <Badge variant={variant.active ? 'default' : 'secondary'}>
          {variant.active ? t('variantsTable.status.active') : t('variantsTable.status.inactive')}
        </Badge>
      );
    },
    meta: {
      label: t('variantsTable.columns.status'),
      variant: 'multiSelect',
      options: [
        { label: t('variantsTable.status.active'), value: 'true' },
        { label: t('variantsTable.status.inactive'), value: 'false' },
      ],
      icon: () => <Tag />,
    },
    enableColumnFilter: false,
    enableSorting: false,
    size: 100,
  },
  {
    id: 'default',
    accessorKey: 'default',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('variantsTable.columns.default')} />,
    cell: ({ row }) => {
      const variant = row.original;
      return variant.default ? <Badge variant="outline">{t('variantsTable.default')}</Badge> : null;
    },
    enableSorting: false,
    meta: { label: t('variantsTable.columns.default') },
    size: 80,
  },
  // Variant ID (moved to end, copyable)
  {
    id: 'id',
    accessorKey: 'id',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('variantsTable.columns.id')} />,
    cell: ({ row }) => {
      const variant = row.original;
      const displayValue = `${variant.id.substring(0, 8)}...`;
      return <CopyableResourceBadge name="Variant ID" value={displayValue} copyValue={variant.id} />;
    },
    enableSorting: true,
    enableHiding: true,
    meta: { label: t('variantsTable.columns.id') },
    size: 140,
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => {
      const variant = row.original;
      const { storeRoutes, navigate } = useStoreNavigate();
      return (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(storeRoutes.VARIANT_DETAILS(productId, variant.id))}
        >
          <ExternalLink className="h-4 w-4" />
        </Button>
      );
    },
    enableSorting: false,
    enableHiding: false,
    size: 60,
  },
], [t, productId, storeId]);
};
